/**
 * Device token storage shared across the language subdomains.
 *
 * localStorage is per-origin, so mandarin.language-island.com and
 * german.language-island.com would otherwise each demand their own sign-in —
 * five logins for one account. The token is therefore mirrored into a cookie
 * scoped to the parent domain, which every subdomain can read.
 *
 * The cookie is not HttpOnly, which is the same exposure localStorage already
 * has: the app itself must be able to read the token to send it. It buys
 * nothing to lock the cookie down while the identical value sits in
 * localStorage beside it.
 */

const COOKIE = 'li_session'
const MAX_AGE = 365 * 24 * 60 * 60

/** The registrable parent, so the cookie spans every language subdomain. */
function cookieDomain(hostname: string): string | null {
  const parts = hostname.split('.')
  if (parts.length < 2) return null
  // Never set a domain cookie for localhost or a bare host.
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return null
  return `.${parts.slice(-2).join('.')}`
}

export function readSessionCookie(): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${COOKIE}=`))
  if (!match) return null
  const value = decodeURIComponent(match.slice(COOKIE.length + 1))
  return value || null
}

export function writeSessionCookie(token: string | null): void {
  if (typeof document === 'undefined') return

  const domain = cookieDomain(window.location.hostname)
  const scope = domain ? `; domain=${domain}` : ''
  // Secure only over https, so `wrangler pages dev` on http still works.
  const secure = window.location.protocol === 'https:' ? '; secure' : ''

  if (!token) {
    document.cookie = `${COOKIE}=; path=/${scope}; max-age=0; samesite=lax${secure}`
    return
  }
  document.cookie = `${COOKIE}=${encodeURIComponent(token)}; path=/${scope}; max-age=${MAX_AGE}; samesite=lax${secure}`
}
